import { Indicator } from "@/components/ui/indicator";
import { ReactionScore } from "@/api/studio/insights/types/metrics.type";
import { InfoModalTrigger } from "@/components/ui/modal/info-modal-trigger";
import { ReactionInfoModal } from "@/features/reports/modals/reaction-info-modal";

/* Prop Types */
interface ReactionTileProps {
  reaction: ReactionScore;
}

/* Reaction label mappings */
const reactionLabels: Record<ReactionScore, string> = {
  1: "Negative",
  2: "Neutral",
  3: "Mixed",
  4: "Positive",
};

/* Reaction description mappings */
const reactionDescriptions: Record<ReactionScore, string> = {
  1: "The overall positive or negative reaction towards an asset.",
  2: "We didn't find a strong overall predictive positive or negative reaction towards an asset.",
  3: "We found a mix of elements in the asset that predict both positive and negative reactions.",
  4: "The overall positive or negative reaction towards an asset.",
};

/* Reaction color mappings */
const getReactionColor = (reaction: ReactionScore): string => {
  switch (reaction) {
    case 4:
      return "#80B918"; // Positive
    case 3:
      return "#8C8C90"; // Mixed
    case 2:
      return "#8C8C90"; // Neutral
    case 1:
      return "#DC3545"; // Negative
    default:
      return "#8C8C90"; // Fallback
  }
};

/*
 * ReactionTile component
 *
 * This component displays the reaction metric for an asset showing positive, negative, neutral, or mixed reactions.
 * The reaction value comes from the API as 1-4 and is mapped to the appropriate display.
 */
export const ReactionTile = ({ reaction = 1 }: ReactionTileProps) => {
  /* Consts */
  const label = reactionLabels[reaction];
  const description = reactionDescriptions[reaction];
  const color = getReactionColor(reaction);

  /* Render */
  return (
    <div className="p-4 rounded-lg bg-base-200 flex flex-col gap-2 my-4">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-1">
          <p className="font-semibold">Reaction</p>
          <InfoModalTrigger modalContent={<ReactionInfoModal />} iconClassName="size-4" />
        </div>
        <div className="flex flex-row items-center gap-2 px-1.5 py-0.5">
          <div className="size-3.5 flex-shrink-0 flex items-center justify-center">
            <Indicator color={color} size="small" />
          </div>
          <p className="font-semibold text-sm">{label}</p>
        </div>
      </div>
      <p className="text-sm text-base-content/70">{description}</p>
    </div>
  );
};
